/**
 * World theming — the two (and a half) "worlds" of the platform each have a
 * distinct accent identity, so the user always knows which space they're in:
 *
 *   • operator      — indigo/violet  (the platform console)
 *   • organization  — green/gold     (a customer's event workspace)
 *   • attendee      — teal           (the public guest hub)
 *
 * Tokens are applied as CSS variables on <html> by useWorldTheme, so a world
 * switch animates (the accent eases from indigo → green). Components can read
 * either the CSS var (var(--ep-accent)) or the returned token object for inline
 * styles.
 */

export type WorldKey = 'operator' | 'organization' | 'attendee';

export interface WorldTheme {
  key: WorldKey;
  label: string;
  accent: string;      // primary accent
  accentDark: string;  // pressed / strong
  accentSoft: string;  // tint background
  onAccent: string;    // text on accent
  heroFrom: string;    // hero gradient start
  heroTo: string;      // hero gradient end
  glow: string;        // hero glow color (rgba)
}

export const WORLDS: Record<WorldKey, WorldTheme> = {
  operator: {
    key: 'operator',
    label: 'Operator',
    accent: '#4f46e5',
    accentDark: '#4338ca',
    accentSoft: '#eef2ff',
    onAccent: '#ffffff',
    heroFrom: '#1e1b4b',
    heroTo: '#312e81',
    glow: 'rgba(139,92,246,0.55)',
  },
  organization: {
    key: 'organization',
    label: 'Organization',
    accent: '#1a6b3c',
    accentDark: '#15532f',
    accentSoft: '#ecfdf5',
    onAccent: '#ffffff',
    heroFrom: '#0f2d1f',
    heroTo: '#1a6b3c',
    glow: 'rgba(201,168,76,0.40)',
  },
  attendee: {
    key: 'attendee',
    label: 'Attendee',
    accent: '#0f766e',
    accentDark: '#0b5a54',
    accentSoft: '#ecfeff',
    onAccent: '#ffffff',
    heroFrom: '#042f2e',
    heroTo: '#0f766e',
    glow: 'rgba(45,212,191,0.40)',
  },
};

/** Map a world theme to the CSS-variable record applied on the root element. */
export function worldCssVars(t: WorldTheme): Record<string, string> {
  return {
    '--ep-accent': t.accent,
    '--ep-accent-dark': t.accentDark,
    '--ep-accent-soft': t.accentSoft,
    '--ep-on-accent': t.onAccent,
    '--ep-hero-from': t.heroFrom,
    '--ep-hero-to': t.heroTo,
    '--ep-glow': t.glow,
  };
}
